import EntryModal from "@components/EntryModal";
import GameNavbar from "@components/GameNavbar";
import NascarFooter from "@components/NascarFooter";
import NascarNav from "@components/NascarNav";
import useClientContext, { setModal } from "@hooks/useClientContext";
import useGame from "@hooks/useGame";
import useGigya from "@hooks/useGigya";
import useSession from "@hooks/useSession";
import { useEffect, useState } from "react";
import { breakpoints, FONTS, media, pxToRem } from "src/styles/styleUtils";
import styled, { ThemeProvider } from "styled-components";
import { publicBasePath } from "../styles/styleUtils";
import TopBannerAd from "./ads/TopBannerAd";

export const theme = {
  fontPrimary: `${FONTS.PRIMARY}`,
  fontSecondary: `${FONTS.SECONDARY}`,
  contentMaxWidth: breakpoints.contentMaxWidth, // Pages may change this as needed per designs
  grey_100: "#c1c2c3",
  grey_150: "#a2a4a4",
  grey_200: "#8e9091",
  grey_300: "#808080",
  grey_400: "#5a5a5a",
  grey_500: "#333333",
  grey_600: "#1b1b1b",
  grey_700: "#10181F",
  grey_blue: "#22303c",
  blue: "#005eb8",
  blueAlt: "#007bbf",
  gold: "#f3d03e",
  goldAlt: "#f5ce3e",
  orange: "#e7720d",
  red: "#ea0029",
  green: "#319731",
};

const PageContainer = styled.div`
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-flow: column;
  background-color: ${theme.grey_700};
  padding-bottom: ${pxToRem(128)};

  ${media.mobile} {
    padding-bottom: ${pxToRem(72)};
  }
`;

const MainContentArea = styled.main`
  flex: 0 1 auto;
  background-image: url("${publicBasePath}/images/NCS_RainBackground.jpg");
  background-size: cover;
  background-repeat: no-repeat;
`;

/**
 * Commonly used to wrap sections of content with our maxWidth value.
 */
export const ContentSection = styled.section`
  width: 100%;
  max-width: ${theme.contentMaxWidth};
  margin: 0 auto;
`;

/**
 * Wraps all pages and provides the standard app layout.
 */
function LayoutProvider({ children }) {
  const { refetch: fetchGame } = useGame();
  const { getGigyaAccount } = useGigya();
  const [{ openModal, isNative }, dispatch] = useClientContext();
  const { data: session } = useSession();
  const [shouldShowAd, setShouldShowAd] = useState(true);

  const isLoggedIn = Boolean(session?.user?.token);
  const hasNoEntry = !session.entries.chase?.length;
  const isEntryGatedModal = ["CREATE_LEAGUE", "JOIN_LEAGUE"].includes(openModal.name);

  useEffect(() => {
    fetchGame(); // initial fetch for game.json
  }, [fetchGame]);

  useEffect(() => {
    getGigyaAccount(); // initial check for a user's account
  }, [getGigyaAccount]);

  useEffect(() => {
    if (isLoggedIn && hasNoEntry) {
      dispatch(setModal("CREATE_ENTRY")); // show EntryModal if logged in without an entry
    }
  }, [isLoggedIn, hasNoEntry, dispatch, isEntryGatedModal]);

  return (
    <ThemeProvider theme={theme}>
      <PageContainer>
        {shouldShowAd && <TopBannerAd setShouldShowAd={setShouldShowAd} />}
        {!isNative && <NascarNav />}
        <GameNavbar />
        <MainContentArea>
          <ContentSection>{children}</ContentSection>
        </MainContentArea>
        <EntryModal />
      </PageContainer>
      {!isNative && <NascarFooter />}
    </ThemeProvider>
  );
}

export default LayoutProvider;
